import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useImmer } from 'use-immer';
import lodash from 'lodash';
import classnames from 'classnames';
import styles from './AddExpectationsPanel.scss';
import DismissiblePanel from 'components/common/DismissiblePanel';
import DocumentTypes from './DocumentTypes/DocumentTypes';
import AssignRoles from './AssignRoles/AssignRoles';

import {
    addStudyExpectations,
    loadStudyExpectations,
    loadStaffDocumentTypes,
} from 'pages/studyOverview/actions/studyExpectations';

import { getStudyRoles } from 'pages/studyOverview/actions/studySettings';


import {
    peek,
    removeSelectedOptionsFromAllDocumentTypes,
    getRolesAssignedToActiveDocumentType,
    mapRolesToDocumentTypes,
    getAvailableStudyRolesWithAssigned,
    getDocumentTypesWithAssignedRoles,
    markStaffDocumentTypes,
    getDocumentType,
    getMappedDocTypes
} from './Helpers';
import { loadHomePageV4Access } from '../../../../../common/features/predicates';

const STEPS = {
    documentTypes: 'document-types',
    assignRoles: 'assign-roles',
};

export function AddExpectationsPanel({
    className,
    loadStudyExpectations,
    loadStaffDocumentTypes,
    getStudyRoles,
    addStudyExpectations,
    onDismiss,
    availableStudyRoles,
    availableDocumentTypes,
    allStudyRolesAssigned,
    show,
    study,
    complion4FlagEnabled
}) {

    const [stepStack, setStepStack] = useImmer([STEPS.documentTypes]);
    const resetStepStack = () => setStepStack(() => [STEPS.documentTypes]);
    const pushStep = (stepName) => setStepStack((steps) => ([...steps, stepName]));
    const popStep = () => setStepStack((steps) => {
        steps.pop();
        return steps;
    });

    const [selectedDocumentTypes, setDocumentTypes] = useImmer([]);
    const [activeDocumentTypeId, setActiveDocTypeId] = useImmer();
    const [rolesToDocumentType, setRolesToDocumentType] = useImmer(allStudyRolesAssigned);

    useEffect(() => {
        loadStudyExpectations();
        loadStaffDocumentTypes();
        getStudyRoles(study.institutionId);

    }, []);

    useEffect(()=>{
        setRolesToDocumentType(state=>allStudyRolesAssigned);
    }, [allStudyRolesAssigned, show]);

    const removeDocumentType = (docTypeId) => {
        setRolesToDocumentType(state =>
            state.filter(roleToDocumentType => roleToDocumentType.documentTypeId !== docTypeId)
        );
        setDocumentTypes(
            docTypes => docTypes.filter(docType => docType.id !== docTypeId)
        );
    };

    const onAssignRoleClicked = (documentType) => {
        setActiveDocTypeId(() => documentType.documentTypeId);
        pushStep(STEPS.assignRoles);
    };

    const onAssignRoles = (roles) => {
        var foundEntry = lodash.find(
            rolesToDocumentType,
            roleToDocumentType => roleToDocumentType.documentTypeId == activeDocumentTypeId
        );
        if (!foundEntry) {
            setRolesToDocumentType((state) => [
                ...state, {
                    documentTypeId: activeDocumentTypeId,
                    roles: roles
                }
            ]);
        } else {
            setRolesToDocumentType(
                (state) => state.map(
                    roleToDocumentType => roleToDocumentType.documentTypeId == activeDocumentTypeId
                        ? { ...roleToDocumentType, roles }
                        : roleToDocumentType
                )
            );
        }
        popStep();
    };

    const dismissPanel = () => {
        setRolesToDocumentType(state => []);
        setDocumentTypes(state => []);
        resetStepStack();
        onDismiss();
    };

    const onAssignExpectations = () => {
        const docTypesWithRoles = mapRolesToDocumentTypes(selectedDocumentTypes, rolesToDocumentType);       
        const mappedDocTypes = getMappedDocTypes(docTypesWithRoles, availableStudyRoles, allStudyRolesAssigned);        
        addStudyExpectations(mappedDocTypes);
        dismissPanel();
    };

    const onGoBack = () => {
        if (!showGoBackButton) return null;
        return popStep;
    };

    let child;
    let showGoBackButton = false;
    let assignedRoles = [];
    let availableRoles = [];
    let initiallyAssignedRoles = [];
    const hasStudyRoles = availableStudyRoles.length > 0;
    switch (peek(stepStack)) {
        case STEPS.assignRoles:
            showGoBackButton = true;
            assignedRoles = getRolesAssignedToActiveDocumentType(rolesToDocumentType, activeDocumentTypeId);
            availableRoles = getAvailableStudyRolesWithAssigned(availableStudyRoles, assignedRoles);
            initiallyAssignedRoles = assignedRoles.filter(role => role.isInitiallyAssigned);
            child = <AssignRoles
                availableRoles={availableRoles}
                documentType={getDocumentType(availableDocumentTypes, activeDocumentTypeId)}
                onAssignRoles={onAssignRoles}
                assignedRoles={assignedRoles}
                initiallyAssignedRoles={initiallyAssignedRoles}
                onCancel={dismissPanel}
            />;
            break;
        default:
            child = <DocumentTypes
                onDismiss={onDismiss}
                availableDocumentTypes={availableDocumentTypes}
                documentTypes={selectedDocumentTypes}
                rolesToDocumentType={rolesToDocumentType}
                onAssignRoleClicked={onAssignRoleClicked}
                onRemoveDocumentType={removeDocumentType}
                setDocumentTypes={setDocumentTypes}
                onCancel={dismissPanel}
                hasStudyRoles={hasStudyRoles}
                onSave={onAssignExpectations}
                complion4FlagEnabled={complion4FlagEnabled}
            />;
            break;
    }

    return (
        <DismissiblePanel
            className={classnames(styles.addExpectationsPanel, className)}
            dismiss={!show}
            onDismiss={dismissPanel}
            onGoBack={onGoBack()}
        >
            {child}
        </DismissiblePanel>
    );
}

export default connect((state) => {
    const study = lodash.get(state, 'study', {});
    const documentTypesFromBinder = lodash.get(state, 'binder.layout.documentTypes', []);
    const documentTypes = markStaffDocumentTypes(documentTypesFromBinder, lodash.get(state, 'staffDocumentTypes', []));
    const selectedDocumentTypes = lodash.get(state, 'study.studyExpectations', []);
    const filteredDocumentTypes = removeSelectedOptionsFromAllDocumentTypes(documentTypes, selectedDocumentTypes);
    const studyRoles = lodash.orderBy(
        lodash.get(state, 'studySettings.studyRoles'),
        [studyRole => studyRole.name.toLowerCase()]
    );
    const staffDocumentExpectations = lodash.get(state, 'study.staffDocumentExpectations', []);
    const allStudyRolesAssigned = getDocumentTypesWithAssignedRoles(staffDocumentExpectations, studyRoles);
    const availableStudyRoles = lodash.uniqBy(studyRoles, role => role.id);

    return {
        study,
        availableDocumentTypes: filteredDocumentTypes || [],
        availableStudyRoles: availableStudyRoles,
        allStudyRolesAssigned,
        complion4FlagEnabled: loadHomePageV4Access(state),
    };
}, {
    addStudyExpectations,
    loadStudyExpectations,
    loadStaffDocumentTypes,
    getStudyRoles
})(AddExpectationsPanel);
